import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import PlanPayment from "containers/PlanPayment";
import PlanAndPricing from "containers/PlanAndPricing";
import Stack from "components/common/Stack";
import Box from "components/common/Box";
import Text from "components/common/Text";
import Image from "components/common/Image";

import { thirdpartyActions } from "stores/thirdparty/thirdpartyActions";
import { jarvsiOnestockOnboardingActions } from "stores/jarvisOnestock/jarvisOnestockOnboarding/jarvisOnestockOnboardingAction";
import { onboardingActions } from "stores/onboarding/onboardingActions";
import { config } from "config";
import { localStore } from "stores/localStorage";
import { getAllInOnePreferredFrequency } from "helper/allInOneSignupParams";
import { pickAllInOnePlan } from "helper/subscriptionPlan";

/**
 * All-in-one route: light shell (logo / breadcrumb / stepper) + full payment UI.
 *
 * Reuse: `PlanPayment` is the default export from `containers/PlanPayment/index.js`,
 * i.e. `PlanPaymentStore(PlanPaymentContainer)` — same class as PlanPaymentContainer.jsx
 * but **with Redux** (plans, payment methods, actions). Do not render
 * `PlanPaymentContainer` alone; it will miss required props and break.
 */
const colors = {
  primary: "#2B70C5",
  primaryTint: "rgba(43, 112, 197, 0.04)",
  primarySoft: "rgba(43, 112, 197, 0.08)",
  orange: "#E0953D",
  orangeTint: "rgba(224, 149, 61, 0.12)",
  grey: "#A2A7AE",
  darkGrey: "#676E76",
  black: "#303236",
  divider: "#DEDEDE",
};

const STEPS = [
  { id: 1, label: "Payment", active: true },
  { id: 2, label: "Product Selection", active: false },
  { id: 3, label: "CKYC", active: false },
  { id: 4, label: "Link broker", active: false },
];

/** All-in-one uses OneStock plans (not Sentiment / Portfolio from localStorage). */
const ALL_IN_ONE_PRODUCT_CODE = "ALLINONE";


const AllInOne = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const onboardingPlanId = useSelector((state) => state.onboarding.planId);
  const planIdForPayment = onboardingPlanId || null;
  const subscriptionPlans = useSelector((state) => state.thirdparty.subscriptionPlans);
  const isPlansLoading = useSelector(
    (state) => state.thirdparty.loading.subscriptionPlans === 1,
  );
  const plansFetchCompleted = useSelector(
    (state) => state.thirdparty.isInitialCalled.subscriptionPlans === 1,
  );
  const customerValueAddedPlanId = useSelector(
    (state) => state.jarvisOnestock.onestockOnboarding.customerValueAddedPlanId
  );
  const isAllInOneSubscriptionActive = useSelector(
    (state) => state.onboarding.isAllInOneSubscriptionActive
  );
  const isInitialFetched = useSelector(
    (state) => state.onboarding.isInitialFetched
  );
  const sourceType = useSelector(
    (state) => state.onboarding.sourceType
  );

  const preferredFrequency = getAllInOnePreferredFrequency();

  /** Prefer requested id; if API has no match, use first plan so PlanPayment mounts with valid data */
  const resolvedPlanId = useMemo(() => {
    if (!subscriptionPlans?.length) {
      return planIdForPayment;
    }
    const picked = pickAllInOnePlan(subscriptionPlans, {
      planId: planIdForPayment,
      frequency: preferredFrequency,
    });
    if (picked) {
      return String(picked.planId);
    }
    const want = String(planIdForPayment ?? "");
    const match = subscriptionPlans.find((p) => String(p.planId) === want);
    if (match) {
      return String(match.planId);
    }
    return String(subscriptionPlans[0].planId);
  }, [subscriptionPlans, planIdForPayment, preferredFrequency]);

  useEffect(() => {
    const productKey = Object.keys(config.productCodes).find(
      (k) => config.productCodes[k] === ALL_IN_ONE_PRODUCT_CODE,
    );
    if (productKey) {
      localStore.setActiveProduct(productKey);
    }
    dispatch(thirdpartyActions.getSubscriptionPlans({ productCode: ALL_IN_ONE_PRODUCT_CODE }));
    dispatch(jarvsiOnestockOnboardingActions.getOnboardingData());
    dispatch(onboardingActions.getOnboardingData());
  }, [dispatch]);

  useEffect(() => {
    if (isInitialFetched === 1) {
      if (
        !sourceType ||
        String(sourceType).toUpperCase() === "NORMAL" ||
        String(sourceType).toUpperCase() === "NULL"
      ) {
        history.replace("/customer/dashboard");
      }
    }
  }, [isInitialFetched, sourceType, history]);

  useEffect(() => {
    if (
      isAllInOneSubscriptionActive === 1 ||
      isAllInOneSubscriptionActive === true ||
      isAllInOneSubscriptionActive === "1"
    ) {
      history.replace("/customer/dashboard"); // new code to redirect if any onboarding completed  /customer/product-info
    }
  }, [isAllInOneSubscriptionActive, history]);

  const handlePayNowClick = (paymentData, isOnetime) => {
    localStore.resetPaymentInitiated();
    history.push({
      pathname: "/customer/onetime-payment",
      state: {
        returnURI: "/all-in-one",
        productCode: "ALL_IN_ONE",
        planId: paymentData?.planId ?? resolvedPlanId,
        offerCode: paymentData?.offerCode,
        paymentMethod: paymentData?.paymentMethod,
        paymentMethodOption: paymentData?.paymentMethodOption,
        paymentType: "INITIAL",
        paymentDetails: {
          authType: paymentData?.authType,
          accountNumber: paymentData?.accountNumber,
          accountType: paymentData?.accountType,
          accountHolderName: paymentData?.accountHolderName,
          ifscCode: paymentData?.ifscCode,
        },
      },
    });
  };

  const showPlansLoading =
    isPlansLoading || (!plansFetchCompleted && !subscriptionPlans?.length);
  const showPlansEmpty =
    !isPlansLoading && plansFetchCompleted && !subscriptionPlans?.length;
  const showPlanPayment = !isPlansLoading && subscriptionPlans?.length > 0;

  return (
    <Box sx={{ bgcolor: "#fff", minHeight: "100vh" }}>
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          borderBottom: `1px solid ${colors.divider}`,
          px: { xs: 2, md: "120px" },
          py: 1.5,
          minHeight: 72,
        }}
      >
        <Stack spacing={0.5}>
          <Image
            src={'/jarvis-logo.svg'}
            alt={config.companyName}
            rootStyle={{ height: 32, maxWidth: 180 }}
            imgStyle={{ height: "100%", width: "auto", objectFit: "contain" }}
          />
        </Stack>
      </Stack>
      {/* <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        rowGap={1}
        columnGap={1}
        sx={{
          px: { xs: 2, md: "120px" },
          py: 0.5,
          bgcolor: colors.primaryTint,
          borderRadius: 1,
          mx: { xs: 1, md: 0 },
        }}
      >
        {STEPS.map((step) => (
          <Stack
            key={step.id}
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ py: 1.75 }}
          >
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: step.active ? colors.primarySoft : "#fff",
                border: step.active ? "none" : `1px solid ${colors.divider}`,
              }}
            >
              <Text
                variant="caption2"
                sx={{
                  fontWeight: 600,
                  color: step.active ? colors.primary : colors.divider,
                  lineHeight: 1,
                }}
              >
                {step.id}
              </Text>
            </Box>
            <Text
              variant="small"
              sx={{
                fontWeight: 500,
                color: step.active ? colors.primary : colors.grey,
              }}
            >
              {step.label}
            </Text>
          </Stack>
        ))}
      </Stack> */}
      <Box sx={{ px: { xs: 2, md: "120px" }, py: 4 }}>
        <PlanAndPricing
          productCode={ALL_IN_ONE_PRODUCT_CODE}
          paymentType="INITIAL"
          selectedPlanId={resolvedPlanId}
          isAllInOne={true}
          initialCouponCode="ALL_IN_ONE"
          customerValueAddedPlanId={customerValueAddedPlanId}
        />
      </Box>
    </Box>
  );
};

export default AllInOne;
